import type { FC, ReactNode } from 'react'
import { Briefcase, Send, FileText, Clock3, Lock } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Button, Loader } from '@/components/ui'
import { useTenderStats } from '@/lib/api/hooks/tender'

interface StatsCardProps {
	title: string
	value: string | number
	icon: ReactNode
	iconContainerClassName: string
	iconClassName: string
	description?: string
}

interface StatConfig {
	key: string
	title: string
	icon: LucideIcon
	iconContainerClassName: string
	iconClassName: string
	description?: string
}

const StatCard = ({
	title,
	value,
	icon,
	iconContainerClassName,
	iconClassName,
	description
}: StatsCardProps) => {
	return (
		<article className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
			<div className="grid grid-cols-[auto_1fr] gap-4">
				<div className={`flex h-12 w-12 xl:w-16 xl:h-16 items-center justify-center rounded-md ${iconContainerClassName}`}>
					<span className={iconClassName}>{icon}</span>
				</div>
				<div>
					<p className="text-base font-medium text-gray-600">{title}</p>
					<p className="text-[2rem] leading-[1.1] font-semibold text-gray-900">{value}</p>
					{description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
				</div>
			</div>
		</article>
	)
}

const STATS_CONFIG: StatConfig[] = [
	{
		key: 'total',
		title: 'Total Tenders',
		icon: Briefcase,
		iconContainerClassName: 'bg-[#F8E8EB80]',
		iconClassName: 'text-[#8F2F44]',
		description: ''
	},
	{
		key: 'totalPublished',
		title: 'Published',
		icon: Send,
		iconContainerClassName: 'bg-[#E8F6EE80]',
		iconClassName: 'text-[#0F8A47]',
		description: ''
	},
	{
		key: 'totalDrafts',
		title: 'Drafts',
		icon: FileText,
		iconContainerClassName: 'bg-[#FDF3E680]',
		iconClassName: 'text-[#B86A00]',
		description: ''
	},
	{
		key: 'totalEndingSoon',
		title: 'Closing Soon',
		icon: Clock3,
		iconContainerClassName: 'bg-[#FDF3E680]',
		iconClassName: 'text-[#C2650A]',
		description: ''
	},
	{
		key: 'totalClosed',
		title: 'Closed',
		icon: Lock,
		iconContainerClassName: 'bg-[#F3F4F680]',
		iconClassName: 'text-[#6B7280]',
		description: ''
	},
]

const TendersStats: FC = () => {
	const { data: stats, isLoading, isError, refetch } = useTenderStats()

	if (isError) {
		return (
			<div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4 sm:col-span-2 xl:col-span-5">
				<p>Error loading statistics.</p>
				<Button onClick={() => refetch()} className="px-4 py-2 bg-forestGreen-100 text-white rounded" loading={isLoading}>
					Retry
				</Button>
			</div>
		)
	}

	if (isLoading) {
		return (
			<div className="flex items-center justify-center">
				<Loader size="md" variant="wine" message="Loading statistics..." />
			</div>
		)
	}

	const values = {
		total: stats?.totalTender ?? 0,
		totalPublished: stats?.publishedTender ?? 0,
		totalDrafts: stats?.draftTender ?? 0,
		totalEndingSoon: stats?.closingSoonTender ?? 0,
		totalClosed: stats?.closedTender ?? 0,
	}

	const getDescription = (key: string) => {
		const closedTenderPercentage = stats?.closedTender &&   stats?.totalTender ? (stats.closedTender / stats.totalTender) * 100 : 0
		switch (key) {
			case 'total':
				return `All Time`
			case 'totalPublished':
				return `${stats?.publishedPercentageOfTotal ?? 0}% of total`
			case 'totalDrafts':
				return `${stats?.draftPercentageOfTotal ?? 0}% of total`
			case 'totalEndingSoon':
				return `${stats?.closingSoonPercentageOfTotal ?? 0}% of total`
			case 'totalClosed':
				return `${closedTenderPercentage.toFixed(2)}% of total`
			default:
				return ''
		}
	};

	return (
		<section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 2xl:w-3/4">
			{STATS_CONFIG.map((config) => {
				const Icon = config.icon
				return (
					<StatCard
						key={config.key}
						title={config.title}
						value={values[config.key as keyof typeof values]}
						icon={<Icon className="w-6 h-6 xl:w-8 xl:h-8" strokeWidth={1.5} />}
						iconContainerClassName={config.iconContainerClassName}
						iconClassName={config.iconClassName}
						description={getDescription(config.key)}
					/>
				)
			})}
		</section>
	)
}

export default TendersStats
